import { Delete, Document, Renew } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { Button } from '../../lib/button.jsx';
import { TruncateText } from '../../lib/truncate-text.jsx';
import { typographyVariants } from '../../lib/typography.jsx';
import ProgressCircular from '../Progress/ProgressCircular.jsx';
import type { FileUploaderProps } from './FileUploader.jsx';
import { getColorState, isDefined, revokePreviewUrl } from './utils.js';

type ImplicitProps = Parameters<
  NonNullable<FileUploaderProps['renderItem']>
>[0];

type ExplicitProps = {
  className?: string;
  children?: React.ReactNode;
  variant?: FileUploaderProps['itemVariant'];
};

export type FileUploaderItemProps = ImplicitProps & ExplicitProps;

export const FileUploaderItem = ({
  variant = 'medium',
  ...rest
}: FileUploaderItemProps) => {
  const componentsVariantsMap = {
    small: SmallFileItem,
    medium: MediumFileItem,
  };

  const VariantComponent = componentsVariantsMap[variant];

  return <VariantComponent {...rest} />;
};

type FileItemVariantProps = Omit<FileUploaderItemProps, 'variant'>;

const SmallFileItem = ({
  className,
  baseFile,
  onRemove,
  onRetry,
  children,
}: FileItemVariantProps) => {
  const { name, validationError, uploadError, isUploaded } = baseFile;
  const errorMessage = validationError?.message || uploadError?.message;
  return (
    <li
      data-testid={name}
      data-warning={Boolean(validationError)}
      data-error={Boolean(uploadError)}
      data-uploaded={isUploaded}
      className={cn(
        'flex items-center gap-2 text-text-secondary data-[error=true]:text-error-dark data-[uploaded=true]:text-text-primary data-[warning=true]:text-warning-dark',
        className,
      )}
    >
      <Document />
      <div className="flex min-w-0 flex-col gap-0.5">
        <TruncateText>
          <p
            className={typographyVariants({
              variant: 'bodyShort01',
              color: getColorState(baseFile),
            })}
          >
            {name}
          </p>
        </TruncateText>
        {Boolean(errorMessage) && (
          <TruncateText>
            <p
              className={typographyVariants({
                variant: 'caption2',
                color: getColorState(baseFile),
              })}
            >
              {errorMessage}
            </p>
          </TruncateText>
        )}
      </div>
      <Actions {...{ children, baseFile, onRemove, onRetry }} />
    </li>
  );
};

const MediumFileItem = ({
  className,
  baseFile,
  onRemove,
  onRetry,
  children,
}: FileItemVariantProps) => {
  const { name, validationError, uploadError, isUploaded } = baseFile;

  const errorMessage = validationError?.message || uploadError?.message;

  return (
    <li
      data-testid={name}
      data-warning={baseFile.isLocalInvalidFile() || Boolean(validationError)}
      data-error={baseFile.isLocalErrorFile() || Boolean(uploadError)}
      data-uploaded={isUploaded}
      className={cn(
        'group/uploader-item flex items-center gap-3 rounded-sm bg-ui-secondary pr-2 text-text-secondary data-[error=true]:bg-background-error data-[error=true]:text-error-dark data-[uploaded=true]:text-text-primary data-[warning=true]:bg-background-warning data-[warning=true]:text-warning-dark',
        className,
      )}
    >
      <ImagePreview previewUrl={baseFile.previewUrl} />
      <div className="flex min-w-0 flex-col gap-0.5">
        <TruncateText>
          <p
            className={typographyVariants({
              variant: 'bodyShort01',
              color: getColorState(baseFile),
            })}
          >
            {name}
          </p>
        </TruncateText>
        {Boolean(errorMessage) && (
          <TruncateText>
            <p
              className={typographyVariants({
                variant: 'caption2',
                color: getColorState(baseFile),
              })}
            >
              {errorMessage}
            </p>
          </TruncateText>
        )}
      </div>
      <Actions {...{ children, baseFile, onRemove, onRetry }} />
    </li>
  );
};

const ImagePreview = ({ previewUrl }: { previewUrl?: string }) => {
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm bg-ui-hover">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          onLoad={() => {
            revokePreviewUrl(previewUrl);
          }}
          className="h-12 w-12 rounded-sm object-cover"
        />
      ) : (
        <Document className="fill-inherit text-inherit" />
      )}
    </div>
  );
};

type ActionsProps = Pick<
  FileItemVariantProps,
  'baseFile' | 'onRemove' | 'onRetry' | 'children'
>;
const Actions = ({ children, baseFile, onRemove, onRetry }: ActionsProps) => {
  if (baseFile.isUploading) {
    return <ProgressCircular size="small" />;
  }

  const defaultActions = (
    <>
      {baseFile.isLocalErrorFile() && (
        <Button
          aria-label="Retry"
          icon
          size="small"
          variant="text"
          onClick={onRetry}
          color="error"
        >
          <Renew />
        </Button>
      )}
      <Button
        aria-label="Remove"
        icon
        size="small"
        variant="text"
        onClick={onRemove}
        color={getColorState(baseFile)}
      >
        <Delete />
      </Button>
    </>
  );

  return (
    <div
      className="ml-auto flex items-center gap-1 fill-tertiary-main text-tertiary-main data-[error=true]:fill-error-dark data-[error=true]:text-error-dark data-[uploaded=true]:fill-tertiary-main data-[uploaded=true]:text-text-primary data-[warning=true]:fill-warning-dark data-[warning=true]:text-warning-dark"
      data-warning={baseFile.isLocalInvalidFile()}
      data-error={baseFile.isLocalErrorFile()}
      data-uploaded={baseFile.isUploaded}
    >
      {isDefined(children) ? children : defaultActions}
    </div>
  );
};

export default FileUploaderItem;
